import { InvoiceStatus } from "@wingspanhq/payments/dist/interfaces";
import { WSQueryCache } from "@ws-react-query";
import {
  BulkActionOnProgress,
  BulkActionProgress,
  bulkActionFactory,
  useForegroundBulkAction
} from "../../../modules/BulkActions";
import { QUERY_PAYABLE, QUERY_PAYABLES } from "../../../query/payments/keys";
import { paymentsService } from "../../../services/payments";
import { getAllEntries } from "../../../utils/serviceHelper";
import { PayablesTableItemData } from "../../components/NewPayablesTable/PayablesTable";
import { API_QUERY_PAYABLES_DRAFT } from "./apiQueries";

async function fetchDraftPayables() {
  return getAllEntries(async (...args) => {
    return (await paymentsService.payable.list(...(args as any))).data;
  }, API_QUERY_PAYABLES_DRAFT.filter);
}

async function processOpenPayables(
  data: PayablesTableItemData[],
  onProgress?: BulkActionOnProgress
): Promise<BulkActionProgress> {
  const bulkOpen = bulkActionFactory<PayablesTableItemData>({
    onProgress,
    action: async item => {
      const payableId = item.payable?.payableId || "";
      return await paymentsService.payable.update(payableId, {
        status: InvoiceStatus.Open
      });
    },
    onFinish: ({ successItems }) => {
      WSQueryCache.invalidateQueries(QUERY_PAYABLES);
      successItems.forEach(item => {
        WSQueryCache.invalidateQueries([
          QUERY_PAYABLE,
          item.payable?.payableId
        ]);
      });
    }
  });

  const { successItems, failedItems } = await bulkOpen.run(data);

  return {
    total: data.length,
    processed: successItems.length + failedItems.length,
    successful: successItems.length,
    failed: failedItems.length
  };
}

export function useOpenAllBulkAction() {
  const runBulkAction = useForegroundBulkAction();

  return (itemCount: number) =>
    runBulkAction({
      actionName: "Open payables",
      itemCount,
      confirmDescription: `Are you sure you want to open ${itemCount.toLocaleString()} draft payable${itemCount > 1 ? "s" : ""}?`,
      confirmLabel: "Yes, open",
      getItemCount: data => data.length,
      prepare: async () => {
        const fetched = await fetchDraftPayables();
        return fetched.map<PayablesTableItemData>(payable => ({ payable }));
      },
      process: async (payables, onProgress) => {
        return processOpenPayables(payables, onProgress);
      }
    });
}
